<script>
  import { onMount } from "svelte";
  import { DataTableSkeleton } from "carbon-components-svelte";
  import Header from "lib/components/Header.svelte";
  import { SectionWrapper } from "paxray-components-svelte";
  import ProcessGroup from "./components/ProcessGroup.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";
  import { useNavigate } from "svelte-navigator";

  const navigate = useNavigate();
  const pageSize = 25;
  const groupColors = {
    management: "#abcfe8",
    core: "#b7e3bd",
    support: "#e5e8a8",
  };
  const cardIcons = [
    "target",
    "user",
    "evidence",
    "analytics_dark",
    "applications",
    "cash",
    "group_work",
    "route",
  ];

  let groups = [];
  let catalogRows = [];
  let summary = {};
  let loading = true;
  let error = "";
  let selectedL1 = "";
  let selectedL2 = "";
  let selectedL3 = "";
  let selectedL4 = "";
  let selectedTeam = "";
  let pages = { management: 1, core: 1, support: 1 };

  $: l1Options = uniqueValues(catalogRows.map((row) => row.l1));
  $: l2Options = uniqueValues(catalogRows
    .filter((row) => !selectedL1 || row.l1 === selectedL1)
    .map((row) => row.l2));
  $: l3Options = uniqueValues(catalogRows
    .filter((row) => (!selectedL1 || row.l1 === selectedL1) && (!selectedL2 || row.l2 === selectedL2))
    .map((row) => row.l3));
  $: l4Options = uniqueValues(catalogRows
    .filter((row) => (!selectedL1 || row.l1 === selectedL1) && (!selectedL2 || row.l2 === selectedL2) && (!selectedL3 || row.l3 === selectedL3))
    .map((row) => row.l4));
  $: teamOptions = uniqueValues(catalogRows.flatMap((row) => row.teams || []));
  $: allProcesses = groups.flatMap((group) => group.processes || []);
  $: portfolioScore = calculatePortfolioScore(allProcesses);
  $: processMaturity = {
    score: portfolioScore,
    tone: portfolioScore === null ? "warning" : portfolioScore >= 70 ? "positive" : portfolioScore >= 40 ? "warning" : "negative",
    groups: groups.map((group) => ({
      label: group.label.replace(" Processes", ""),
      score: calculatePortfolioScore(group.processes || []),
      tooltip: `Average performance score for the ${group.label.toLowerCase()} process group.`,
    })),
  };
  $: processMetrics = [
    { label: "Processes (L1)", value: summary.l1_count || 0, tooltip: "Number of top-level processes in the portfolio." },
    { label: "Tasks (L4)", value: summary.l4_count || 0, tooltip: "Number of lowest-level tasks in the portfolio." },
    { label: "Annual Time", value: formatDuration(summary.time_pa), tooltip: "Total observed active time across all processes in one year." },
  ];
  $: teamCount = uniqueValues(catalogRows.flatMap((row) => row.teams || [])).length;
  $: timeDistribution = groups.map((group) => {
    const value = (group.processes || []).reduce((sum, process) => sum + Number(process.time_pa || 0), 0);
    const total = allProcesses.reduce((sum, process) => sum + Number(process.time_pa || 0), 0);
    return { ...group, value, share: total ? Math.round((value / total) * 100) : 0 };
  });
  $: scopeSubtitleItems = [
    { label: "L1", value: formatCompact(summary.l1_count) },
    { label: "L2", value: formatCompact(summary.l2_count) },
    { label: "L3", value: formatCompact(summary.l3_count) },
    { label: "L4", value: formatCompact(summary.l4_count) },
  ];
  $: hasActiveFilters = Boolean(selectedL1 || selectedL2 || selectedL3 || selectedL4 || selectedTeam);

  onMount(loadOverview);

  async function loadOverview() {
    loading = true;
    error = "";
    try {
      const [overviewResponse, catalogResponse] = await Promise.all([
        fetchWithSession("/api/monitoring/processes/overview"),
        fetchWithSession("/api/monitoring/processes/catalog"),
      ]);
      const [overviewResult, catalogResult] = await Promise.all([
        overviewResponse.json(),
        catalogResponse.json(),
      ]);
      if (!overviewResponse.ok || !catalogResponse.ok) {
        throw new Error(overviewResult.message || catalogResult.message || "Process overview could not be loaded.");
      }
      groups = overviewResult.groups || [];
      summary = overviewResult.summary || {};
      catalogRows = catalogResult.processes || [];
    } catch (loadError) {
      error = loadError.message || "Process overview could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function filteredProcesses(group) {
    if (!hasActiveFilters) return group.processes;
    const matchingNames = new Set(catalogRows
      .filter((row) =>
        (!selectedL1 || row.l1 === selectedL1) &&
        (!selectedL2 || row.l2 === selectedL2) &&
        (!selectedL3 || row.l3 === selectedL3) &&
        (!selectedL4 || row.l4 === selectedL4) &&
        (!selectedTeam || row.teams?.includes(selectedTeam)),
      )
      .map((row) => row.l1));
    return group.processes.filter((process) => matchingNames.has(process.name));
  }

  function visibleProcesses(group) {
    const rows = filteredProcesses(group);
    const start = (pages[group.type] - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }

  function pageCount(group) {
    return Math.max(1, Math.ceil(filteredProcesses(group).length / pageSize));
  }

  function setPage(type, nextPage, group) {
    pages = {
      ...pages,
      [type]: Math.min(Math.max(nextPage, 1), pageCount(group)),
    };
  }

  function resetPages() {
    pages = { management: 1, core: 1, support: 1 };
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right));
  }

  function calculatePortfolioScore(processes) {
    const scored = processes.filter((process) => process.score !== null && process.score !== undefined);
    if (!scored.length) return null;
    const totalWeight = scored.reduce((sum, process) => sum + Math.max(Number(process.time_pa || 0), 1), 0);
    return Math.round(scored.reduce((sum, process) =>
      sum + Number(process.score) * Math.max(Number(process.time_pa || 0), 1), 0) / totalWeight);
  }

  function openCatalog(process) {
    navigate(
      `${ROUTES.monitoring.processCatalog}?l1=${encodeURIComponent(process.name)}`,
    );
  }

  function formatDuration(seconds) {
    const value = Number(seconds || 0);
    const hours = value / 3600;
    if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k h`;
    if (hours >= 1) return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
    return `${Math.round(value / 60)} min`;
  }

  function formatCompact(value) {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value || 0));
  }

  function scoreValue(value) {
    return value === null || value === undefined
      ? "N/A"
      : `${Math.round(Number(value))}`;
  }

  function processCardIcon(index, type) {
    if (type === "management") return "analytics_dark";
    if (type === "support") return "group_work";
    return cardIcons[index % cardIcons.length];
  }

  function coreKpis(process) {
    return [
      { label: "General Score", value: scoreValue(process.score), tooltip: "Overall performance score based on the measured process KPIs.", tone: Number(process.score || 0) >= 70 ? "good" : "watch" },
      { label: "Complexity", value: complexityScore(process), tooltip: `Process scope:\nL1: 1 process\nL2: ${process.l2_count ?? "N/A"} subprocesses\nL3: ${process.l3_count ?? "N/A"} task groups\nL4: ${process.l4_count ?? "N/A"} tasks`, tone: "good" },
      { label: "Activity", value: `${formatDuration(process.time_pa)} · ${formatCompact(process.frequency_pa)} runs`, tone: "good" },
      // "top apps": je Aufgabe zaehlt nur ihre meistgenutzte Anwendung, nicht alle Anwendungen.
      { label: "Reach", value: `${formatCompact(process.users)} users · ${process.application_count ?? "N/A"} top apps`, tone: "good" },
      // Haeufigkeitsgewichtete mittlere Dauer einer Aufgabenausfuehrung.
      { label: "Avg. Duration", value: formatDuration(process.avg_time), tone: "good" },
    ];
  }

  function complexityScore(process) {
    const counts = [process.l2_count, process.l3_count, process.l4_count];
    if (counts.every((value) => value === null || value === undefined)) return "N/A";
    return counts.reduce((sum, value) => sum + Number(value || 0), 0);
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Process Intelligence"
  areaClickable={false}
  breadcrumb="Process Portfolio"
  tooltipText="Portfolio view of the transferred process hierarchy. L1 processes are grouped according to Porter's management, core and support process model; core processes expose their central operational KPIs directly."
/>

<SectionWrapper fitContent fullscreen wrapperClass="process-overview-wrapper" canvasClass="process-overview-canvas">
  <div class="overview" slot="main">
    {#if loading}
      <DataTableSkeleton
        rows={9}
        headers={[
          { key: "process", value: "Process" },
          { key: "type", value: "Type" },
          { key: "kpis", value: "KPIs" },
        ]}
        showToolbar={false}
      />
    {:else if error}
      <div class="empty-state error">{error}</div>
    {:else if !summary.l1_count}
      <div class="empty-state">
        <strong>No process results transferred yet</strong>
        <span>Run the calculated monitoring transfer again to add the L1–L4 process hierarchy.</span>
      </div>
    {:else}
      <div class="overview-layout">
        <div class="portfolio-content">
          {#each groups as group}
            <ProcessGroup
              group={{ ...group, color: groupColors[group.type] }}
              filteredProcesses={filteredProcesses(group)}
              visibleProcesses={visibleProcesses(group)}
              currentPage={pages[group.type]}
              pageSize={pageSize}
              totalPages={pageCount(group)}
              timeValue={timeDistribution.find((item) => item.type === group.type)?.value}
              timeShare={timeDistribution.find((item) => item.type === group.type)?.share || 0}
              groupScore={calculatePortfolioScore(group.processes || [])}
              {hasActiveFilters}
              {formatDuration}
              {processCardIcon}
              {coreKpis}
              onProcessClick={openCatalog}
              onPageChange={(nextPage) => setPage(group.type, nextPage, group)}
            />
          {/each}
        </div>
      </div>
    {/if}
  </div>
</SectionWrapper>

<style>
  :global(.process-overview-wrapper .view_wrapper),
  :global(.process-overview-wrapper .process-overview-canvas) {
    background: var(--pax-smoke);
  }

  :global(.process-overview-wrapper .process-overview-canvas) {
    padding: 0;
    width: 100%;
    min-height: calc(100vh - 8rem);
  }

  .overview {
    display: flex;
    min-height: 100%;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    padding-bottom: 1rem;
  }

  .overview-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
    gap: 1rem;
    width: 100%;
    flex: 1;
    min-height: 100%;
  }

  .portfolio-content {
    display: flex;
    min-width: 0;
    min-height: 100%;
    flex-direction: column;
    gap: 1rem;
  }

  .empty-state {
    display: grid;
    min-height: 20rem;
    place-content: center;
    gap: 0.4rem;
    color: #69727d;
    text-align: center;
  }

  .empty-state strong { color: #262a31; font-size: 1rem; }
  .empty-state span { font-size: 0.75rem; }
  .empty-state.error { color: #da1e28; }

  @media (max-width: 78rem) {
    .overview-layout { grid-template-columns: minmax(0, 1fr); }
  }

  @media (max-width: 64rem) {
    .overview-layout { grid-template-columns: 1fr; }
  }
</style>
